import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { MobileNavigation } from '../components/MobileNavigation';
import { Plus, User, UserCheck, Crown, Trash, Eye, EyeSlash } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { toast } from 'sonner';

import { getApiBaseUrl } from '../utils/api';
export function AdminUsers() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [sendInvite, setSendInvite] = useState(true);  // Default to sending invite
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'staff'
  });
  
  useEffect(() => {
    loadUsers();
  }, []);
  
  const loadUsers = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/users`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Load users error:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error('Please fill in name and email');
      return;
    }
    
    // If not sending invite, password is required
    if (!sendInvite && !formData.password) {
      toast.error('Password is required when not sending invite');
      return;
    }
    
    try {
      const payload = {
        ...formData,
        send_invite: sendInvite
      };
      
      // Don't send password if using invite flow
      if (sendInvite) {
        delete payload.password;
      }
      
      const response = await fetch(`${getApiBaseUrl()}/api/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const result = await response.json();
        setUsers([result.user, ...users]);
        setShowAddDialog(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          password: '',
          role: 'staff'
        });
        setSendInvite(true);
        
        if (result.user.invite_sent) {
          toast.success(`Invitation email sent to ${formData.email}!`);
        } else {
          toast.success(`${formData.role === 'admin' ? 'Admin' : 'Staff'} user created!`);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create user');
      }
    } catch (error) {
      console.error('Create user error:', error);
      toast.error('Failed to create user');
    }
  };
  
  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}?`)) return;
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setUsers(users.filter(u => u.user_id !== userId));
        toast.success('User deleted');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Delete user error:', error);
      toast.error('Failed to delete user');
    }
  };
  
  const resendInvite = async (userId, email) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/users/${userId}/resend-invite`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success(`Invitation email resent to ${email}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to resend invite');
      }
    } catch (error) {
      console.error('Resend invite error:', error);
      toast.error('Failed to resend invite');
    }
  };
  
  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Crown size={18} className="text-primary" weight="duotone" />;
      case 'staff': return <UserCheck size={18} className="text-green-500" weight="duotone" />;
      default: return <User size={18} className="text-blue-500" weight="duotone" />;
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-20" data-testid="admin-users">
      {/* Header */}
      <div className="glass-effect border-b border-border sticky top-0 z-40">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-heading">STAFF MANAGEMENT</h1>
              <p className="text-sm text-muted-foreground">
                {users.length} staff/admin user{users.length !== 1 ? 's' : ''}
              </p>
            </div>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-user-btn">
                  <Plus size={20} weight="bold" className="mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading">CREATE STAFF USER</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Enter full name"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>Email Address *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="Enter email"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>Phone Number</Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  
                  <div>
                    <Label>Role *</Label>
                    <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="staff">Staff Member</SelectItem>
                        <SelectItem value="admin">Administrator</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Invite Option */}
                  <div className="flex items-center space-x-2 p-3 rounded-lg bg-muted/50">
                    <input
                      type="checkbox"
                      id="send-invite"
                      checked={sendInvite}
                      onChange={(e) => setSendInvite(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="send-invite" className="text-sm cursor-pointer flex-1">
                      Send invitation email to set their own password
                    </Label>
                  </div>
                  
                  {/* Only show password field if not sending invite */}
                  {!sendInvite && (
                    <div>
                      <Label>Password *</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          placeholder="Create a password"
                          required={!sendInvite}
                          minLength={6}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        You'll need to share this password with the user
                      </p>
                    </div>
                  )}
                  
                  <div className="flex gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 btn-primary">
                      {sendInvite ? 'Send Invite' : 'Create User'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="px-4 py-6">
        {users.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="text-center py-12">
              <UserCheck size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading text-lg mb-2">NO STAFF USERS</h3>
              <p className="text-muted-foreground">
                Add staff or admin users to help manage the program.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {users.map((user) => (
              <Card key={user.user_id} className="bg-card border-border">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        {getRoleIcon(user.role)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{user.name}</h3>
                          {user.account_status === 'pending_invite' && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-500">
                              Pending
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        user.role === 'admin' 
                          ? 'bg-primary/20 text-primary' 
                          : 'bg-green-500/20 text-green-500'
                      }`}>
                        {user.role.toUpperCase()}
                      </span>
                      {user.account_status === 'pending_invite' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resendInvite(user.user_id, user.email)}
                          className="text-xs"
                        >
                          Resend
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteUser(user.user_id, user.name)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      >
                        <Trash size={16} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      <MobileNavigation userRole="admin" />
    </div>
  );
}
